import React, { useState, useEffect, useContext } from 'react';
import Turnstile from 'react-turnstile';
import { isRutValid, sanitizeRutInput, RUT_MAX_LENGTH } from '../../utils';
import useLegalUrl from '../hooks/useLegalUrl';
import usePersonalData from '../hooks/usePersonalData';
import useStationsData from '../hooks/useStationsData';
import useCarsData from '../hooks/useCarsData';
import useTimeData from '../hooks/useTimeData';
import useDistanceData from '../hooks/useDistanceData';
import useDownpaymentData from '../hooks/useDownpaymentData';
import useSimulationData from '../hooks/useSimulationData';
import useTotalData from '../hooks/useTotalData';
import TimeAndModel from './TimeAndModel';
import CurrencyFormat from './utils/CurrencyFormat';
import { getPdf } from './utils/getPdf';
import { CotizadorContext } from '../context';
import axios from 'axios';
import ModalContactMe from './modal/ModalContactMe';

const TwoStepForm = ({ uf_value }) => {
    const [state, setState] = useContext(CotizadorContext);
    const [currentStep, setCurrentStep] = useState(1);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [sendContactForm, setSendContactForm] = useState(false);
    const [showContactModal, setShowContactModal] = useState(false);
    const [values, setValues] = useState({
        id: 0,
        full_name: "",
        email: "",
        rut: "",
        phone_number: "",
        city_id: 0,
        city: "",
        station: 0,
        region_id: 0,
        region: "",
        model: "",
        period: "",
        kms: "",
        purchase_timeframe: "",
    });

    const PURCHASE_TIMEFRAME_OPTIONS = [
        "En 1 semana",
        "Entre 1 y 3 meses",
        "Solo estoy cotizando",
    ];

    const { stations } = useStationsData();
    const { getPersonalData, setPersonalData } = usePersonalData();
    const { getSelectedCar } = useCarsData();
    const { getSelectedTime } = useTimeData();
    const { getSelectedDistance } = useDistanceData();
    const { downpayment_percentage } = useDownpaymentData();
    const { simulation_results } = useSimulationData();
    const { getTotal } = useTotalData();
    const links = useLegalUrl();

    const [checkboxValue, setCheckboxValue] = useState(false);
    const [filteredStations, setFilteredStations] = useState(stations);
    const [btn, setBtn] = useState(false);
    const [error, setError] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState(false);

    const sitekey = document.getElementById("recaptcha_sitekey")?.getAttribute('value');

    const uniqueRegions = [];
    stations?.forEach(station => {
        const region = station.city.region;
        if (region) {
            const existingRegionIndex = uniqueRegions.findIndex(r => r.id === region.id);
            if (existingRegionIndex === -1) {
                uniqueRegions.push(region);
            }
        }
    });
    uniqueRegions.sort((a, b) => a.name.localeCompare(b.name));

    const handleOnFormChange = (e) => {
        if (e.target.value) {
            e.target.classList.add('-interacted');
        } else {
            e.target.classList.remove('-interacted');
        }
    }

    const handleInput = (e) => {
        let inputName = e.target.name;
        let value = e.target.value;

        if (inputName === "region") {
            uniqueRegions?.forEach(region => {
                if (region.id == value) {
                    setValues({ ...values, region: region.name, region_id: region.id, station: 0 });
                }
            });
        } else if (inputName === "station") {
            setValues({ ...values, station: Number(value) });
        } else if (inputName === "rut") {
            setValues({ ...values, rut: sanitizeRutInput(value) });
        } else {
            setValues({ ...values, [inputName]: value });
        }
    }

    const handleCheckboxChange = (e) => {
        setCheckboxValue(e.target.checked);
    }

    const handlePurchaseTimeframe = (option) => {
        setValues({ ...values, purchase_timeframe: option });
    }

    const handleTokenSubmit = (token) => {
        fetch("/recaptcha/", {
            method: "POST",
            body: JSON.stringify({ token: token }),
            headers: {
                "Content-Type": "application/json",
                'X-CSRFToken': document.querySelector('meta[name="csrf-token"]').content
            },
        })
            .then(response => response.json())
            .then((data) => {
                if (data.success) {
                    setTurnstileToken(true);
                }
            })
            .catch((error) => {
                console.log("ERROR", error);
            });
    }

    useEffect(() => {
        if (stations && values.region_id) {
            setFilteredStations(stations.filter(station => {
                return station.city.region.id == values.region_id;
            }));
        }

        // El RUT se valida siempre con isRutValid, pero el mensaje de error solo se
        // muestra cuando ya se escribió un RUT completo (el más corto válido tiene 8 caracteres)
        const rutValid = isRutValid(values.rut);
        const rutError = !rutValid && values.rut.length >= 8;

        setError(rutError);
        // Si Turnstile está habilitado, requerirlo. Si está comentado, no lo requerimos
        const turnstileRequired = sitekey ? turnstileToken : true;
        setBtn(values.full_name && values.email && rutValid && values.phone_number &&
              values.region && values.station && values.purchase_timeframe && checkboxValue && turnstileRequired);
    }, [values, checkboxValue, stations, turnstileToken]);

    const handleContinueToStep2 = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!isRutValid(values.rut)) {
            setError(true);
            return;
        }

        try {
            // Obtener los datos del carro, tiempo y distancia seleccionados
            const selectedCar = getSelectedCar();
            const selectedTime = getSelectedTime();
            const selectedDistance = getSelectedDistance();
            
            // Preparar los datos completos para enviar
            const dataToSend = {
                ...values,
                // Solo incluir model si hay un carro seleccionado (como número), sino null
                model: selectedCar ? selectedCar.id : null,
                period: selectedTime ? selectedTime.value : null,
                kms: selectedDistance ? selectedDistance.value : null
            };
            
            // Crear el lead primero antes de pasar al paso 2
            const response = await axios.post('/api/kinto/forms/one_personal/', dataToSend, {
                headers: {
                    'X-CSRFToken': document.querySelector('meta[name="csrf-token"]').content
                }
            });

            // Actualizar el id con la respuesta del servidor
            const updatedValues = { ...dataToSend, id: response.data.id };
            setValues(updatedValues);
            // Actualizar el contexto directamente sin llamar a setPersonalData (que haría otro POST)
            setState(prevState => ({
                ...prevState,
                personal_data: updatedValues
            }));

            dataLayer.push({
                'event': 'trackEvent',
                'eventCategory': 'cotizar_one_personal',
                'eventAction': 'cta',
                'eventLabel': 'continuar_cotizador'
            });

            setCurrentStep(2);
        } catch (error) {
            console.error('Error al crear el lead:', error);
            // Puedes mostrar un mensaje de error al usuario aquí si lo deseas
        }
    };

    const handleBackToStep1 = () => {
        setCurrentStep(1);
    };

    const handleCancel = () => {
        // Reset form or redirect
        setValues({
            id: 0,
            full_name: "",
            email: "",
            rut: "",
            phone_number: "",
            city_id: 0,
            city: "",
            station: 0,
            region_id: 0,
            region: "",
            model: "",
            period: "",
            kms: "",
            purchase_timeframe: "",
        });
        setCheckboxValue(false);
        setTurnstileToken(false);
    };

    const downloadPdf = async () => {
        if (selectedCar && !downloadingPdf && hasValidSimulation) {
            setDownloadingPdf(true);
            try {
                const dataToSend = {
                    car: state.selected_car,
                    time: state.selected_time,
                    distance: state.selected_distance,
                    downpayment: state.downpayment_percentage,
                    monthlyFee: getTotal(),
                    carImg: selectedCar.photo_1.url
                        ? selectedCar.photo_1.url
                        : `${window._env_.STATIC_HOST}/static/kinto/img/cotizador/default_selected_car.png`,
                };
                const { data } = await getPdf(
                    dataToSend.car,
                    dataToSend.time,
                    dataToSend.distance,
                    dataToSend.downpayment,
                    dataToSend.monthlyFee,
                    dataToSend.carImg
                );

                // Crear blob con el PDF
                const file = new Blob([data], { type: "application/pdf" });
                const fileURL = URL.createObjectURL(file);
                
                // Crear enlace temporal para descargar
                const link = document.createElement('a');
                link.href = fileURL;
                link.download = `cotizacion-kinto-${selectedCar.name.replace(/\s+/g, '-')}.pdf`;
                document.body.appendChild(link);
                link.click();
                
                // Limpiar
                document.body.removeChild(link);
                URL.revokeObjectURL(fileURL);

                // SEO google dataLayer
                dataLayer.push({
                    event: "trackEvent",
                    eventCategory: "cotizar_one_personal",
                    eventAction: "cta",
                    eventLabel: "descargar_detalle",
                });
            } catch (error) {
                console.error("Error al descargar el archivo:", error);
            } finally {
                setDownloadingPdf(false);
            }
        }
    };

    const handleContactModalSuccess = () => {
        setShowContactModal(false);
        setSendContactForm(true);
    };

    useEffect(() => {
        setSendContactForm(false);
    }, [getPersonalData()]);

    // Efecto unificado para actualizar todos los datos del cotizador cuando cualquiera cambie
    useEffect(() => {        
        // Obtener todos los valores dentro del useEffect para que sean reactivos
        const selectedCar = getSelectedCar();
        const selectedTime = getSelectedTime();
        const selectedDistance = getSelectedDistance();
        const personalData = getPersonalData();
        
        // Solo proceder si estamos en el paso 2 (cuando hay ID)
        if (personalData.id !== 0) {
            // Preparar los datos actualizados con todos los campos del cotizador
            const updatedData = {
                ...personalData,
                model: selectedCar ? selectedCar.id : personalData.model,
                period: selectedTime ? selectedTime.value : personalData.period,
                kms: selectedDistance ? selectedDistance.value : personalData.kms
            };
            
            // Actualizar vía API
            setPersonalData(updatedData);
        } else if (selectedCar || selectedTime || selectedDistance) {
            // Si no hay ID aún, solo actualizar el estado local
            setState(prevState => ({
                ...prevState,
                personal_data: {
                    ...prevState.personal_data,
                    model: selectedCar ? selectedCar.id : prevState.personal_data.model,
                    period: selectedTime ? selectedTime.value : prevState.personal_data.period,
                    kms: selectedDistance ? selectedDistance.value : prevState.personal_data.kms
                }
            }));
        } else {
            console.log('⚠️ No se cumplieron las condiciones para actualizar');
        }
    }, [state.selected_car, state.selected_time, state.selected_distance, state.personal_data.id]); // Escuchar cambios directamente del estado

    const selectedCar = getSelectedCar();
    const hasValidSimulation = simulation_results !== null && getTotal() > 0;

    // Cuota en UF: usa el valor exacto importado (amount_uf); si no existe, lo deriva del total
    const amountUf = simulation_results?.amount_uf ?? (uf_value ? getTotal() / uf_value : null);
    const amountUfLabel = amountUf
        ? Number(amountUf).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '-';

    return (
        <>
            <div className="step-indicator">
                <button 
                    className="back-button" 
                    onClick={currentStep === 2 ? handleBackToStep1 : null}
                    style={{ display: currentStep === 1 ? 'none' : 'flex' }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <p>Volver atrás</p>
                </button>
                <span className="step-text">Paso {currentStep} de 2</span>
            </div>
            <div className="two-step-form">
                <div className="two-step-form-content">

                    <div className='quoter-header'>
                        <h2>Cotiza tu plan KINTO ONE PERSONAL</h2>
                        <p>
                            {currentStep === 1 
                                ? "Dejanos tus datos y sigue con la simulación en el cotizador."
                                : "Elige modelo, duración y kilometraje para obtener tu estimación."
                            }
                        </p>
                    </div>


                    {currentStep === 1 ? (
                        <form onSubmit={handleContinueToStep2} onChange={handleOnFormChange} className="quoter-form step-one-form">
                            <div className="form-row-group">
                                <h3 className="section-title --text-header">Datos de contacto</h3>
                                
                                <div className="form-row">
                                    <label htmlFor="full_name">Nombre y apellido*</label>
                                    <input
                                        type="text"
                                        id="full_name"
                                        className="form-control"
                                        name="full_name"
                                        placeholder="Ej.: Luis Salas"
                                        value={values.full_name}
                                        onChange={handleInput}
                                        required
                                    />
                                </div>

                                <div className="form-row">
                                    <label htmlFor="email">Email*</label>
                                    <input
                                        type="email"
                                        id="email"
                                        className="form-control"
                                        name="email"
                                        placeholder="Ej.: nombre@correo.com"
                                        value={values.email}
                                        onChange={handleInput}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-row-group">
                                <div className="form-row">
                                    <label htmlFor="phone_number">Teléfono*</label>
                                    <input
                                        type="tel"
                                        id="phone_number"
                                        className="form-control"
                                        name="phone_number"
                                        placeholder="Ej.: +56 9 1234 5678"
                                        value={values.phone_number}
                                        onChange={handleInput}
                                        required
                                    />
                                </div>

                                <div className="form-row">
                                    <label htmlFor="rut">RUT*</label>
                                    <input
                                        type="text"
                                        id="rut"
                                        name="rut"
                                        placeholder="Ej.: 14737553-0"
                                        value={values.rut}
                                        onChange={handleInput}
                                        className={`form-control ${error ? 'error' : ''}`}
                                        maxLength={RUT_MAX_LENGTH}
                                        required
                                    />
                                    {error && <span className="error-message">RUT Inválido</span>}
                                </div>
                            </div>

                            <div className="form-row-group">
                                <h3 className="section-title --text-header">Preferencias de atención</h3>
                                
                                <div className="form-row">
                                    <label htmlFor="region">Región*</label>
                                    <select
                                        id="region"
                                        className="form-control"
                                        name="region"
                                        value={values.region_id}
                                        onChange={handleInput}
                                        required
                                    >
                                        <option value="">Selecciona una opción</option>
                                        {uniqueRegions.map((reg) => (
                                            <option key={reg.id} value={reg.id}>{reg.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-row">
                                    <label htmlFor="station">Sucursal*</label>
                                    <select
                                        id="station"
                                        className="form-control"
                                        name="station"
                                        value={values.station}
                                        onChange={handleInput}
                                        disabled={!values.region_id}
                                        required
                                    >
                                        <option value="">Selecciona una sucursal</option>
                                        {filteredStations.map((station) => (
                                            <option key={station.id} value={station.id}>{station.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-row-group">
                                <h3 className="section-title --text-header">¿Cuándo quieres iniciar el proceso de compra? *</h3>

                                <div className="purchase-timeframe-options">
                                    {PURCHASE_TIMEFRAME_OPTIONS.map((option) => (
                                        <button
                                            key={option}
                                            type="button"
                                            className={`purchase-timeframe-option${values.purchase_timeframe === option ? ' -selected' : ''}`}
                                            onClick={() => handlePurchaseTimeframe(option)}
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="form-checkbox">
                                <label className="checkbox-container">
                                    <input
                                        type="checkbox"
                                        checked={checkboxValue}
                                        onChange={handleCheckboxChange}
                                    />
                                    <span className="checkmark"></span>
                                    <span className="checkbox-label">
                                        Acepto las <a href={links.legal_url} target="_blank" rel="noopener noreferrer">políticas de privacidad</a> y el tratamiento de mis datos.
                                    </span>
                                </label>
                            </div>

                            {sitekey && (
                                <div className="form-turnstile">
                                    <Turnstile
                                        theme="light"
                                        sitekey={sitekey}
                                        onVerify={(token) => handleTokenSubmit(token)}
                                    />
                                </div>
                            )}

                            <div className="quoter-actions">
                                <button
                                    type="button"
                                    className="button --new --light"
                                    onClick={handleCancel}
                                >
                                    CANCELAR
                                </button>
                                <button
                                    type="submit"
                                    className="button --new --dark"
                                    disabled={!btn}
                                >
                                    CONTINUAR AL COTIZADOR
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="step-two-form quoter-form">
                            <TimeAndModel />
                            
                            {hasValidSimulation && (
                                <>
                                    <div className="quoter-result">
                                        <div className="result-label">Total estimado:</div>
                                        <div className="result-price">
                                            <CurrencyFormat value={getTotal()} /> <span>/mes</span> <span style={{ color: '#738184', fontWeight: 300 }}>*</span>
                                        </div>
                                        <div className="result-note">Incluye seguro y asistencia KINTO.</div>
                                        <div className="result-disclaimer">*Calculado con un valor de {amountUfLabel} UF a un precio de UF=${uf_value.toLocaleString('es-CL')}</div>
                                    </div>

                                    <div className="quoter-actions">
                                        <button 
                                            className="button --new --light"
                                            onClick={downloadPdf}
                                            disabled={downloadingPdf || !hasValidSimulation}>
                                            {downloadingPdf ? (
                                                <>
                                                    Generando PDF... <img className="ajax-loader" src={`${window._env_.STATIC_HOST}/static/website/images/ajax-loader.gif`} style={{ display: 'inline', marginLeft: '5px', width: '16px', height: '16px' }} />
                                                </>
                                            ) : (
                                                "Descargar cotización"
                                            )}
                                        </button>
                                        <button
                                            className="button --new --dark"
                                            onClick={() => setShowContactModal(true)}
                                            disabled={sendContactForm || !hasValidSimulation}>
                                            {sendContactForm ? "Formulario enviado ✓" : "Quiero ser contactado"}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {showContactModal && (
                <ModalContactMe
                    onClose={() => setShowContactModal(false)}
                    onSuccess={handleContactModalSuccess}
                />
            )}
        </>
    );
};

export default TwoStepForm;
